import React, { useState, useRef } from 'react';
import { FaCamera, FaSpinner, FaCheck } from 'react-icons/fa';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast'; // Add Toaster import

const ProfileUpload = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.match('image.*')) {
        toast.error('Please select an image file');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) return;
  
    setIsUploading(true);
    const loadingToast = toast.loading('Uploading image...');
  
    try {
      // First, upload to Cloudinary
      const formData = new FormData();
      formData.append('file', selectedImage);
      formData.append('upload_preset', 'africfreshupload');
  
      const cloudinaryResponse = await axios.post(
        'https://api.cloudinary.com/v1_1/dfehxeb5r/image/upload',
        formData
      );
  
      // Get the secure URL from Cloudinary response
      const imageUrl = cloudinaryResponse.data.secure_url;
  
      // Now update the user profile with the new image URL
      const token = localStorage.getItem('AficFreshtoken');
      const updateResponse = await axios.put(
        'https://afric-fresh-backend.onrender.com/user/update-profile-picture',
        { profilePicture: imageUrl },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
  
      if (updateResponse.data.success) {
        setUploadSuccess(true);
        toast.dismiss(loadingToast);
        toast.success('Profile picture updated successfully');
        
        // Update local storage user details
        const currentUser = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
        if (currentUser) {
          // Remove the old lowercase property if it exists
          if (currentUser.profilepicture) {
            delete currentUser.profilepicture;
          }
          // Update the camelCase property
          currentUser.profilePicture = imageUrl;
          localStorage.setItem('AficFreshuserDetails', JSON.stringify(currentUser));
        }
  
        setTimeout(() => setUploadSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
    <Toaster position="top-right" /> {/* Add this line */}
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 max-sm:mt-16">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Profile Picture</h2>
            <p className="mt-2 text-sm text-gray-600">
              Upload a photo of yourself
            </p>
          </div>

          <div className="flex flex-col items-center">
            <div className="relative group">
              <div className={`w-40 h-40 rounded-full overflow-hidden border-4 border-gray-200 
                ${!previewUrl ? 'bg-gray-100' : ''} relative`}>
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Profile preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <FaCamera className="text-gray-400 text-4xl" />
                  </div>
                )}
              </div>
              
              <button
                onClick={() => fileInputRef.current.click()}
                className="absolute bottom-0 right-0 bg-blue-500 p-3 rounded-full text-white 
                  hover:bg-blue-600 transition-colors shadow-lg"
              >
                <FaCamera className="text-xl" />
              </button>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />

            {selectedImage && (
              <div className="mt-6 w-full">
                <div className="mb-4">
                  <p className="text-sm text-gray-500 text-center">
                    Selected: {selectedImage.name}
                  </p>
                </div>

                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  className={`w-full py-3 px-4 rounded-lg text-white font-medium
                    flex items-center justify-center space-x-2 transition-colors
                    ${isUploading || uploadSuccess
                      ? 'bg-green-500'
                      : 'bg-blue-500 hover:bg-blue-600'}`}
                >
                  {isUploading ? (
                    <>
                      <FaSpinner className="animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : uploadSuccess ? (
                    <>
                      <FaCheck />
                      <span>Uploaded!</span>
                    </>
                  ) : (
                    <span>Upload Photo</span>
                  )}
                </button>
              </div>
            )}

            <div className="mt-6">
              <p className="text-xs text-gray-500 text-center">
                Supported formats: JPG, PNG, GIF
                <br />
                Maximum file size: 5MB
              </p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-white rounded-2xl shadow-xl p-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Tips for a great profile photo:
          </h3>
          <ul className="space-y-3 text-sm text-gray-600">
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Use a clear, front-facing photo of your face
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Ensure good lighting and a neutral background
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Avoid using filters or heavy editing
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
              Make sure your face is clearly visible
            </li>
          </ul>
        </div>
      </div>
    </div>

    </>
  );
};

export default ProfileUpload;